<template>
    <section class="endpoint-section endpoint-parameters">
        <header>
            <h4 class="endpoint-section-title">Parameters</h4>
        </header>
        <table v-if="parameters.length" class="endpoint-parameter-table-list">
            <thead>
            <tr class="endpoint-parameter-table-header">
                <th class="endpoint-parameter-heading endpoint-parameter-name">Name</th>
                <th class="endpoint-parameter-heading endpoint-parameter-value">Value</th>
                <th class="endpoint-parameter-heading endpoint-parameter-types">Types</th>
                <th class="endpoint-parameter-heading endpoint-parameter-description">Description</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(parameter, index) in parameters" :key="index" class="endpoint-parameter">
                <td class="endpoint-parameter-cell endpoint-parameter-name">
                    {{ parameter.name }}
                </td>
                <td class="endpoint-parameter-cell endpoint-parameter-value">
                    <component :is="resolveParameterInputType(parameter)"
                               :name="`endpoint-${endpointId}-parameter-${parameter.name}`"
                               v-model="values[parameter.name]"
                               class="endpoint-parameter-value-field"
                               @blob="$emit('blob', $event)"
                    />
                </td>
                <td class="endpoint-parameter-cell endpoint-parameter-types">
                    <template v-for="(propType, index) in parameter.types">
                        <type-link :type="propType" :key="index"/>
                        <span v-if="index < parameter.types.length - 1" class="parameter-type-link-separator">|</span>
                    </template>
                </td>
                <td class="endpoint-parameter-cell endpoint-parameter-description">
                    <markdown v-if="parameter.description.length"
                    >{{ parameter.description.replace(/\n/, ' ') }}
                    </markdown>
                    <span class="data-missing" v-else>No description available</span>
                </td>
            </tr>
            </tbody>
        </table>
        <span v-else class="data-missing">This method has no parameters</span>
    </section>
</template>

<script>
  import Markdown      from 'vue-markdown-v2';
  import CheckboxField from '../Forms/CheckboxField';
  import FileField     from '../Forms/FileField';
  import NumberField   from '../Forms/NumberField';
  import TextField     from '../Forms/TextField';
  import TextAreaField     from '../Forms/TextAreaField';
  import TypeLink      from '../Type/TypeLink';

  export default {
    name: 'EndpointParameters',

    components: {
      TypeLink,
      Markdown,
      CheckboxField,
      NumberField,
      TextField,
      TextAreaField,
      FileField,
    },

    props: {
      endpointId: {
        type:     String,
        required: true,
      },

      parameters: {
        type:     Array,
        required: true,
      },

      values: {
        type:     Object,
        required: true,
      },
    },

    methods: {
      resolveParameterInputType ( parameter ) {
        switch ( parameter.types.join('').toLowerCase() ) {
          case 'number':
          case 'numeric':
          case 'int':
          case 'integer':
          case 'float':
          case 'double':
          case 'long':
            return 'NumberField';

          case 'boolean':
          case 'bool':
            return 'CheckboxField';

          case 'binary':
          case 'blob':
          case 'file':
            return 'FileField';

          case 'array':
          case 'object':
            return 'TextAreaField';

          case 'string':
          default:
            return 'TextField';
        }
      },
    },
  };
</script>

<style scoped>
    .endpoint-parameter-table-list {
        width:           100%;
        border-collapse: collapse;
    }

    .endpoint-parameter-heading,
    .endpoint-parameter-cell {
        padding: 0.5rem;
    }

    .endpoint-parameter-heading:first-child,
    .endpoint-parameter-cell:first-child {
        padding-left: 0;
    }

    .endpoint-parameter-heading {
        text-align:     left;
        border-top:     1px solid var(--color-gray-light);
        border-bottom:  1px solid var(--color-gray-light);
        color:          var(--color-gray-dark);
        font-weight:    600;
        text-transform: uppercase;
    }

    .endpoint-parameter-cell {
        padding:        0.5rem;
        vertical-align: middle;
    }

    .endpoint-parameter-cell >>> p {
        margin: 0;
    }

    .endpoint-parameter-cell >>> p + p {
        margin-top: 0.25rem;
    }

    .endpoint-parameter-cell.endpoint-parameter-name {
        font-family: var(--font-family-monospace);
        font-size:   1.2rem;
        font-weight: 600;
    }

    .endpoint-parameter-value-field {
        max-width: 8rem;
    }

    .data-missing {
        color: var(--color-gray-medium);
    }

    .property-type-link-separator {
        margin: 0 0.25rem;
    }
</style>
